function greetingName(profile) {
  const first = profile?.first_name?.trim()
  if (first) return first
  const full = profile?.full_name?.trim()
  if (full) return full.split(/\s+/)[0]
  return null
}

/**
 * @typedef {{ id: string, title: string, body: string, previewKey?: string }} OrientationStep
 */

/**
 * @param {string | undefined} role
 * @param {object | null} profile
 * @returns {{ title: string, lead: string, steps: OrientationStep[] } | null}
 */
export function getOrientationContent(role, profile = null) {
  const name = greetingName(profile)
  const hi = name ? `Hi ${name}` : 'Welcome'

  if (role === 'client') {
    return {
      title: 'Welcome to your client portal',
      lead: `${hi}. Thanks for joining Social Box.`,
      steps: [
        {
          id: 'cl-1',
          title: 'Your dashboard',
          previewKey: 'actionCenter',
          body: 'See what needs your approval. Each item links to the right campaign and section.',
        },
        {
          id: 'cl-2',
          title: 'Approvals and campaigns',
          previewKey: 'clientQueues',
          body: 'Use Approvals for an inbox-style view, or open My Campaigns to browse everything in one place.',
        },
        {
          id: 'cl-3',
          title: 'Casting and content',
          previewKey: 'phaseStripClient',
          body: 'On each campaign you approve creators in Casting, then review content in Content review after your agency releases it.',
        },
        {
          id: 'cl-4',
          title: 'You stay in your lane',
          body: 'You do not create campaigns or invite creators. Your agency sets up the work; you approve roster and content when it is ready.',
        },
      ],
    }
  }

  if (role === 'admin' || role === 'account_manager') {
    return {
      title: 'Welcome to Social Box',
      lead: `${hi}. Glad you are here.`,
      steps: [
        {
          id: 'am-1',
          title: 'Your dashboard',
          previewKey: 'actionCenter',
          body: 'Priorities show up in the action center. Each row deep-links to the campaign section that needs work.',
        },
        {
          id: 'am-2',
          title: 'Clients',
          previewKey: 'clientList',
          body: 'Add client companies you support. Every campaign belongs to a client.',
        },
        {
          id: 'am-3',
          title: 'Campaigns',
          previewKey: 'campaignList',
          body: 'Create campaigns from the list, then open a campaign for casting, pipeline, and content review.',
        },
        {
          id: 'am-4',
          title: 'Influencers library',
          previewKey: 'castingRoster',
          body: 'Build your creator library here, then add people to Casting on each campaign.',
        },
        {
          id: 'am-5',
          title: 'Campaign workflow',
          previewKey: 'phaseStripAm',
          body: 'Most work on a campaign follows three phases: Casting, Creator pipeline, and Content review.',
        },
        {
          id: 'am-6',
          title: 'Who does what',
          body: 'You set up campaigns and run the workflow. Clients approve casting and content. Creators deliver in My Tasks.',
        },
      ],
    }
  }

  if (role === 'influencer') {
    return {
      title: 'Welcome, creator',
      lead: `${hi}. Glad you are here.`,
      steps: [
        {
          id: 'inf-1',
          title: 'Your dashboard',
          previewKey: 'actionCenter',
          body: 'Invitations, open tasks, and revisions appear here. Use the links to jump to My Tasks.',
        },
        {
          id: 'inf-2',
          title: 'My Tasks',
          previewKey: 'taskList',
          body: 'This is your only upload surface. Each task has a brief and due date. Submit for review when ready.',
        },
        {
          id: 'inf-3',
          title: 'My Campaigns and submissions',
          previewKey: 'influencerQueues',
          body: 'My Campaigns shows invites and campaign context. My Submissions is your history of approved work.',
        },
        {
          id: 'inf-4',
          title: 'After you submit',
          body: 'Your agency reviews first, then the client may approve or request a revision. Revisions return to My Tasks.',
        },
      ],
    }
  }

  return null
}
